// src/pages/CoachHistory.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { getPaymentsByCoach } from "../firebase/payments";
import {
  getFreeRegistrationsByCoach,
  getPrepaidRegistrationsByCoach,
  getRegistrationsByCoach,
  getWeightChangeDeadline,
  canCoachEditRegistration,
  MAX_WEIGHT_CHANGES,
  Registration,
} from "../firebase/registrations";
import { getAllCompetitions } from "../firebase/competitions";
import Navbar from "../components/Navbar";
import EditRegistrationModal from "../components/EditRegistrationModal";
import { C, FONT } from "../theme";
import { generateDirectReceiptPDF } from "../utils/generateReceiptPDF";
import { invalidateCachePrefix } from "../utils/cache";
import { formatDate as formatDateLocalized } from "../utils/dateHelpers";
import { Timestamp } from "firebase/firestore";
import { PencilIcon, RefreshIcon, InfoIcon } from "../components/Icons";

// ── Responsive hook ────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Helper: extract calendar year from a Firestore Timestamp or Date ──────────
function getYear(ts: any): number {
  if (!ts) return 0;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.getFullYear();
}

// ── Per-athlete weight-change budget badge ("n/5") ────────────────────────────
// Green while there's room, amber on the last change, red once exhausted.
function ChangeBudgetBadge({ used, t }: { used: number; t: (k: string) => string }) {
  const left = Math.max(0, MAX_WEIGHT_CHANGES - used);
  const tone =
    left <= 0
      ? { color: "var(--color-danger)", bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.45)" }
      : left <= 1
      ? { color: C.warning, bg: C.warningDim, border: C.warningBorder }
      : { color: "var(--color-success)", bg: "rgba(34,197,94,0.14)", border: "rgba(34,197,94,0.4)" };
  return (
    <span
      title={t("history.athletes.changesTitle").replace("{max}", String(MAX_WEIGHT_CHANGES))}
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: "0.3px",
        color: tone.color,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: 20,
        padding: "2px 8px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {used}/{MAX_WEIGHT_CHANGES}
    </span>
  );
}

// ── Unified row type ───────────────────────────────────────────────────────────
interface UnifiedAthleteRow {
  athleteId?: string;
  slug?: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  category?: string;
  club: string;
  division: string;
  weightCategory: string;
  price: number;
  /** Firestore id of the matching registration document. Used for the coach edit modal. */
  registrationId?: string;
  /** Coach-made weight changes used so far — drives the "n/max" budget badge. */
  weightChangeCount?: number;
}

interface UnifiedRecord {
  id: string;
  referenceCode: string;
  competitionName: string;
  competitionId?: string;
  createdAt: any;
  athleteCount: number;
  totalAmount: number;
  isFree: boolean;
  paymentType: "online" | "prepaid" | "admin_manual";
  athletes: UnifiedAthleteRow[];
  /** Competition snapshot resolved from getAllCompetitions(). Carries weightCategories + weightChangeDeadline. */
  competition?: any;
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function CoachHistory() {
  const { currentUser } = useAuth();
  const { t, language } = useTranslation();
  const w = useWindowWidth();
  const isMobile = w < 768;

  const [records, setRecords] = useState<UnifiedRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{
    record: UnifiedRecord;
    athlete: UnifiedAthleteRow;
    registration: Registration & { id: string };
  } | null>(null);

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Year filter state ──────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // ── New filters: free-text search + payment-type chips ────────────────────
  const [search, setSearch] = useState("");
  type PayChip = "all" | "online" | "prepaid" | "admin_manual";
  const [payChip, setPayChip] = useState<PayChip>("all");

  // Loaded once per page; reused for every record. Keyed by competitionId.
  const [allRegs, setAllRegs] = useState<(Registration & { id: string })[]>([]);

  const loadHistory = (force = false) => {
    if (!currentUser) return;
    // On a manual refresh, drop the cached registration/competition/payment
    // reads so a coach immediately sees admin deletions or their own edits.
    if (force) {
      invalidateCachePrefix("registrations:");
      invalidateCachePrefix("competitions:");
      invalidateCachePrefix("payments:");
    }

    Promise.all([
      getPaymentsByCoach(currentUser.uid),
      getFreeRegistrationsByCoach(currentUser.uid),
      getPrepaidRegistrationsByCoach(currentUser.uid),
      // Pull the coach's full registration set so we can attach a
      // registrationId to every athlete row — required for the edit modal
      // since payments.athletes (online flow) doesn't carry it.
      getRegistrationsByCoach(currentUser.uid),
      // Competitions are needed for the weight-categories dropdown + the
      // weight-change deadline gate.
      getAllCompetitions(),
    ])
      .then(([payments, freeRegs, prepaidRegs, allCoachRegs, comps]) => {
        const compById = new Map<string, any>();
        (comps as any[]).forEach((c) => compById.set(c.id, c));
        setAllRegs(allCoachRegs as (Registration & { id: string })[]);

        // (referenceCode, athleteId) -> registrationId. We use this to enrich
        // the unified rows below — online records pull athletes from the
        // payments collection which has no registration id of its own.
        const regByKey = new Map<string, Registration>();
        for (const r of allCoachRegs) {
          if (r.id) regByKey.set(`${r.referenceCode}::${r.athleteId}`, r);
        }
        const lookupReg = (refCode: string, athleteId: string | undefined) =>
          athleteId ? regByKey.get(`${refCode}::${athleteId}`) : undefined;
        // ── Online (mock payment) records
        const paidRecords: UnifiedRecord[] = payments.map((p) => ({
          id: p.id ?? p.referenceCode,
          referenceCode: p.referenceCode,
          competitionName: p.competitionName,
          competitionId: p.competitionId,
          createdAt: p.createdAt,
          athleteCount: p.athleteCount,
          totalAmount: p.totalAmount,
          isFree: false,
          paymentType: "online" as const,
          competition: p.competitionId ? compById.get(p.competitionId) : undefined,
          athletes: p.athletes.map((a: any) => {
            const reg = lookupReg(p.referenceCode, a.athleteId);
            return {
              athleteId: a.athleteId,
              slug: a.slug,
              firstName: a.firstName,
              lastName: a.lastName,
              dateOfBirth: a.dateOfBirth,
              category: a.category,
              club: a.club,
              division: a.division,
              weightCategory: a.weightCategory,
              price: a.price,
              registrationId: reg?.id,
              weightChangeCount: reg?.weightChangeCount,
            };
          }),
        }));

        // ── Admin manual (bank transfer) records — grouped by referenceCode
        const freeGroups = new Map<string, Registration[]>();
        for (const reg of freeRegs) {
          const arr = freeGroups.get(reg.referenceCode) ?? [];
          arr.push(reg);
          freeGroups.set(reg.referenceCode, arr);
        }
        const manualRecords: UnifiedRecord[] = Array.from(freeGroups.entries()).map(
          ([refCode, regs]) => ({
            id: refCode,
            referenceCode: refCode,
            competitionName: regs[0].competitionName,
            competitionId: regs[0].competitionId,
            createdAt: regs[0].registeredAt,
            athleteCount: regs.length,
            totalAmount: regs.reduce((s, r) => s + (r.price ?? 0), 0),
            isFree: true,
            paymentType: "admin_manual" as const,
            competition: regs[0].competitionId ? compById.get(regs[0].competitionId) : undefined,
            athletes: regs.map((r) => ({
              athleteId: r.athleteId,
              firstName: r.athleteSnapshot.firstName,
              lastName: r.athleteSnapshot.lastName,
              dateOfBirth: r.athleteSnapshot.dateOfBirth,
              category: r.category,
              club: r.club,
              division: r.division,
              weightCategory: r.weightCategory,
              price: r.price ?? 0,
              registrationId: r.id,
              weightChangeCount: r.weightChangeCount,
            })),
          })
        );

        // ── Prepaid (coach bank transfer) records — grouped by referenceCode
        const prepaidGroups = new Map<string, Registration[]>();
        for (const reg of prepaidRegs) {
          const arr = prepaidGroups.get(reg.referenceCode) ?? [];
          arr.push(reg);
          prepaidGroups.set(reg.referenceCode, arr);
        }
        const prepaidRecords: UnifiedRecord[] = Array.from(prepaidGroups.entries()).map(
          ([refCode, regs]) => ({
            id: refCode,
            referenceCode: refCode,
            competitionName: regs[0].competitionName,
            competitionId: regs[0].competitionId,
            createdAt: regs[0].registeredAt,
            athleteCount: regs.length,
            totalAmount: regs.reduce((s, r) => s + (r.price ?? 0), 0),
            isFree: false,
            paymentType: "prepaid" as const,
            competition: regs[0].competitionId ? compById.get(regs[0].competitionId) : undefined,
            athletes: regs.map((r) => ({
              athleteId: r.athleteId,
              firstName: r.athleteSnapshot.firstName,
              lastName: r.athleteSnapshot.lastName,
              dateOfBirth: r.athleteSnapshot.dateOfBirth,
              category: r.category,
              club: r.club,
              division: r.division,
              weightCategory: r.weightCategory,
              price: r.price ?? 0,
              registrationId: r.id,
              weightChangeCount: r.weightChangeCount,
            })),
          })
        );

        // ── Merge and sort all records by date (newest first)
        const all = [...paidRecords, ...manualRecords, ...prepaidRecords].sort((a, b) => {
          const aT = a.createdAt?.toDate?.()?.getTime() ?? 0;
          const bT = b.createdAt?.toDate?.()?.getTime() ?? 0;
          return bT - aT;
        });

        setRecords(all);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    loadHistory(true);
  };

  // Auto-dismiss the success toast.
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(id);
  }, [toast]);

  // ── Derive sorted list of years that have at least one record ─────────────
  const availableYears: number[] = Array.from(
    new Set(records.map((r) => getYear(r.createdAt)).filter((y) => y > 0))
  ).sort((a, b) => b - a); // newest first

  // Always include currentYear even if no records yet (so the tab is always
  // present and the page always has a sensible default).
  if (!availableYears.includes(currentYear)) {
    availableYears.unshift(currentYear);
  }

  // ── Filtered records for the selected year + search + chip ────────────────
  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((r) => {
      if (getYear(r.createdAt) !== selectedYear) return false;
      if (payChip !== "all" && r.paymentType !== payChip) return false;
      if (q) {
        const hay = [
          r.competitionName,
          r.referenceCode,
        ]
          .map((x) => (x ?? "").toLowerCase())
          .join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [records, selectedYear, search, payChip]);

  // ── Stats — computed from filteredRecords only ─────────────────────────────
  const totalAthletes = filteredRecords.reduce((s, r) => s + r.athleteCount, 0);
  const onlineTotal = filteredRecords
    .filter((r) => r.paymentType === "online")
    .reduce((s, r) => s + r.totalAmount, 0);
  const bankTotal = filteredRecords
    .filter((r) => r.paymentType === "admin_manual")
    .reduce((s, r) => s + r.totalAmount, 0);
  const prepaidTotal = filteredRecords
    .filter((r) => r.paymentType === "prepaid")
    .reduce((s, r) => s + r.totalAmount, 0);

  const formatDate = (ts: any): string => formatDateLocalized(ts, language);

  const handleEditAthlete = (record: UnifiedRecord, athlete: UnifiedAthleteRow) => {
    if (!athlete.registrationId) {
      console.warn("No registrationId on athlete row; cannot open edit modal.");
      return;
    }
    const reg = allRegs.find((r) => r.id === athlete.registrationId);
    if (!reg) {
      console.warn("Registration not found for id:", athlete.registrationId);
      return;
    }
    setEditing({ record, athlete, registration: reg });
  };

  // Apply an edit locally without refetching — keeps the UI snappy and
  // matches the optimistic pattern used in the admin pages.
  const handleEditSaved = (registrationId: string, updates: Partial<Registration>) => {
    // When the weight changed, mirror the server-side stamps into local state
    // so reopening the modal in this session immediately reflects the cooldown
    // AND the incremented change-budget (weightChangeCount), without a refetch.
    const weightChanged = updates.weightCategory !== undefined;
    setAllRegs((prev) =>
      prev.map((r) => {
        if (r.id !== registrationId) return r;
        const next = { ...r, ...updates } as Registration & { id: string };
        if (weightChanged) {
          next.weightLastChanged = Timestamp.now();
          next.weightChangedBy = currentUser?.displayName || currentUser?.email || "";
          next.weightChangeCount = (r.weightChangeCount ?? 0) + 1;
        }
        return next;
      })
    );
    setRecords((prev) =>
      prev.map((rec) => ({
        ...rec,
        athletes: rec.athletes.map((a) =>
          a.registrationId === registrationId
            ? {
                ...a,
                division: (updates.division as any) ?? a.division,
                weightCategory: updates.weightCategory ?? a.weightCategory,
                club: updates.club ?? a.club,
                category: updates.category ?? a.category,
                price: updates.price ?? a.price,
                weightChangeCount: weightChanged
                  ? (a.weightChangeCount ?? 0) + 1
                  : a.weightChangeCount,
              }
            : a
        ),
      }))
    );
  };

  const downloadReceiptPDF = async (record: UnifiedRecord) => {
    setGeneratingId(record.id);
    try {
      await generateDirectReceiptPDF({
        referenceCode: record.referenceCode,
        competitionName: record.competitionName,
        createdAt: record.createdAt,
        totalAmount: record.totalAmount,
        paymentType:
          record.paymentType === "admin_manual"
            ? "bank"
            : record.paymentType === "prepaid"
            ? "bank"
            : "website",
        coachName: currentUser?.displayName ?? undefined,
        coachEmail: currentUser?.email ?? undefined,
        athletes: record.athletes,
      });
    } catch (err) {
      console.error("[PDF] Generation failed:", err);
      alert(`PDF generation failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setGeneratingId(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={{ ...s.container, padding: isMobile ? "24px 12px 48px" : "40px 24px" }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={{ ...s.header, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 20 : 24 }}>
          <div>
            <div style={s.pageLabel}>{t("history.pageLabel")}</div>
            <div style={{ ...s.heading, fontSize: isMobile ? "28px" : "36px" }}>
              {t("history.heading")}
            </div>
            <div style={s.subheading}>{t("history.subheading")}</div>
          </div>

          {!loading && filteredRecords.length > 0 && (
            <div style={{ ...s.statsRow, flexDirection: "row", flexWrap: "wrap" }}>
              <div style={s.statBox}>
                <div style={s.statNumber}>{totalAthletes}</div>
                <div style={s.statLabel}>{t("history.total.registrations")}</div>
              </div>
              {onlineTotal > 0 && (
                <div style={s.statBox}>
                  <div style={{ ...s.statNumber, color: C.red }}>{onlineTotal} GEL</div>
                  <div style={s.statLabel}>{t("history.total.online")}</div>
                </div>
              )}
              {bankTotal > 0 && (
                <div style={s.statBox}>
                  <div style={{ ...s.statNumber, color: C.blue }}>{bankTotal} GEL</div>
                  <div style={s.statLabel}>{t("history.total.manual")}</div>
                </div>
              )}
              {prepaidTotal > 0 && (
                <div style={s.statBox}>
                  <div style={{ ...s.statNumber, color: C.blue }}>{prepaidTotal} GEL</div>
                  <div style={s.statLabel}>{t("history.total.prepaid")}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Year filter bar ──────────────────────────────────────────────── */}
        {!loading && availableYears.length > 0 && (
          <div style={s.yearRow}>
            <span style={s.yearRowLabel}>{t("history.year") || "Year"}</span>
            <div style={s.yearBtnGroup}>
              {availableYears.map((year) => {
                const isActive = year === selectedYear;
                return (
                  <button
                    key={year}
                    style={{
                      ...s.yearBtn,
                      ...(isActive ? s.yearBtnActive : {}),
                    }}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
            <button
              style={{ ...s.refreshBtn, marginLeft: "auto", opacity: refreshing ? 0.6 : 1 }}
              onClick={handleRefresh}
              disabled={refreshing}
              title={t("history.refresh")}
            >
              <RefreshIcon size={14} style={{ ...(refreshing ? { animation: "spin 0.7s linear infinite" } : {}) }} />
              {refreshing ? t("history.refreshing") : t("history.refresh")}
            </button>
          </div>
        )}

        {/* ── Search + payment-type chips ─────────────────────────────────── */}
        {!loading && records.length > 0 && (
          <div style={s.filterBar}>
            <input
              type="text"
              placeholder={t("history.filter.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={s.filterSearch}
            />
            <div style={s.chipGroup}>
              {([
                ["all",          t("common.filter.all")],
                ["online",       t("common.filter.online")],
                ["prepaid",      t("common.filter.prepaid")],
                ["admin_manual", t("common.filter.bank")],
              ] as [PayChip, string][]).map(([key, label]) => (
                <button
                  key={key}
                  style={{
                    ...s.chip,
                    ...(payChip === key ? s.chipActive : {}),
                  }}
                  onClick={() => setPayChip(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {loading && (
          <div style={s.stateBox}>
            <div style={s.spinner} />
            <div style={{ ...s.stateText, marginTop: 16 }}>{t("history.loading")}</div>
          </div>
        )}

        {/* ── Empty (no records at all) ─────────────────────────────────────── */}
        {!loading && records.length === 0 && (
          <div style={s.stateBox}>
            <div style={s.emptyIcon}>📋</div>
            <div style={s.stateText}>{t("history.empty.title")}</div>
            <div style={s.stateSub}>{t("history.empty.sub")}</div>
          </div>
        )}

        {/* ── Empty (records exist but none for selected year) ──────────────── */}
        {!loading && records.length > 0 && filteredRecords.length === 0 && (
          <div style={s.stateBox}>
            <div style={s.emptyIcon}>📅</div>
            <div style={s.stateText}>
              {t("history.empty.year") || `No registrations in ${selectedYear}`}
            </div>
            <div style={s.stateSub}>
              {t("history.empty.yearSub") || "Try selecting a different year above."}
            </div>
          </div>
        )}

        {/* ── Records ──────────────────────────────────────────────────────── */}
        {!loading && filteredRecords.length > 0 && (
          isMobile
            ? <MobileCardList
                records={filteredRecords}
                generatingId={generatingId}
                formatDate={formatDate}
                t={t}
                onDownload={downloadReceiptPDF}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                onEditAthlete={handleEditAthlete}
                coachId={currentUser?.uid ?? ""}
              />
            : <DesktopTable
                records={filteredRecords}
                generatingId={generatingId}
                formatDate={formatDate}
                t={t}
                onDownload={downloadReceiptPDF}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                onEditAthlete={handleEditAthlete}
                coachId={currentUser?.uid ?? ""}
              />
        )}
      </div>

      {/* ── Coach edit modal ──────────────────────────────────────────────── */}
      {editing && (
        <EditRegistrationModal
          registration={editing.registration}
          weightCategories={
            editing.record.competition?.weightCategories ?? { male: [], female: [] }
          }
          types={editing.record.competition?.types ?? []}
          mode="coach"
          weightChangeDeadline={
            editing.record.competition
              ? getWeightChangeDeadline(editing.record.competition)
              : null
          }
          onClose={() => setEditing(null)}
          onSaved={(updates) => {
            handleEditSaved(editing.registration.id, updates);
            setEditing(null);
            setToast(t("history.editSuccess"));
          }}
        />
      )}

      {/* Success toast */}
      {toast && (
        <div style={s.toast} role="status">
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

// ── Mobile card list ───────────────────────────────────────────────────────────
function MobileCardList({
  records,
  generatingId,
  formatDate,
  t,
  onDownload,
  expandedIds,
  onToggleExpand,
  onEditAthlete,
  coachId,
}: {
  records: UnifiedRecord[];
  generatingId: string | null;
  formatDate: (ts: any) => string;
  t: (k: string) => string;
  onDownload: (r: UnifiedRecord) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onEditAthlete: (r: UnifiedRecord, a: UnifiedAthleteRow) => void;
  coachId: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {records.map((record) => {
        const isGenerating = generatingId === record.id;
        const expanded = expandedIds.has(record.id);
        const accentColor =
          record.paymentType === "prepaid"
            ? C.blue
            : record.paymentType === "admin_manual"
            ? C.blue
            : C.red;
        const canEditAny = canCoachEditRegistration(
          { coachId } as any,
          record.competition,
          coachId
        );
        const wcd = record.competition ? getWeightChangeDeadline(record.competition) : null;
        const deadlineLabel = wcd ? formatDate(wcd) : null;

        return (
          <div key={record.id} style={{ ...ms.card, borderLeftColor: accentColor }}>

            {/* Top row: competition + date */}
            <div style={ms.topRow}>
              <div style={ms.compName}>{record.competitionName}</div>
              <div style={ms.date}>{formatDate(record.createdAt)}</div>
            </div>

            {/* Middle row: ref code + type badge */}
            <div style={ms.midRow}>
              <span style={ms.refCode}>{record.referenceCode}</span>
              {record.paymentType === "admin_manual" && (
                <span style={ms.bankBadge}>{t("history.adminLabel")}</span>
              )}
              {record.paymentType === "prepaid" && (
                <span style={ms.prepaidBadge}>{t("history.prepaid")}</span>
              )}
            </div>

            {/* Bottom row: athlete count + amount + PDF button */}
            <div style={ms.bottomRow}>
              <div style={ms.bottomLeft}>
                <div style={ms.athletePill}>
                  <span style={ms.athletePillNum}>{record.athleteCount}</span>
                  <span style={ms.athletePillLabel}>{t("history.table.athletes")}</span>
                </div>
                <div style={{ ...ms.amount, color: accentColor }}>
                  {record.paymentType === "admin_manual" && record.totalAmount === 0
                    ? <span style={ms.manualBadge}>{t("history.adminLabel")}</span>
                    : `${record.totalAmount} GEL`}
                </div>
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                <button
                  style={ms.expandBtn}
                  onClick={() => onToggleExpand(record.id)}
                >
                  {expanded ? "▲" : "▼"}
                </button>
                <button
                  style={{ ...ms.pdfBtn, opacity: isGenerating ? 0.6 : 1, cursor: isGenerating ? "not-allowed" : "pointer" }}
                  onClick={() => !isGenerating && onDownload(record)}
                  disabled={isGenerating}
                >
                  {isGenerating ? "⏳" : "↓ PDF"}
                </button>
              </div>
            </div>

            {/* Expanded athletes panel */}
            {expanded && (
              <div style={ms.expandedPanel}>
                {!canEditAny && deadlineLabel && (
                  <div style={ms.deadlineHint}>
                    {t("history.athletes.editClosed").replace("{date}", deadlineLabel)}
                  </div>
                )}
                {canEditAny && deadlineLabel && (
                  <div style={ms.editOpenHint}>
                    <InfoIcon size={13} color={C.blue} style={{ marginTop: 1 }} />
                    <span>{t("history.athletes.editOpen").replace("{date}", deadlineLabel)}</span>
                  </div>
                )}
                {record.athletes.map((a, i) => (
                  <div key={(a.registrationId ?? "") + i} style={ms.athleteRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={ms.athleteName}>{a.firstName} {a.lastName}</div>
                      <div style={ms.athleteMeta}>
                        {a.division} · {a.weightCategory}
                        {a.category ? ` · ${a.category}` : ""}
                      </div>
                    </div>
                    {canEditAny && a.registrationId && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <ChangeBudgetBadge used={a.weightChangeCount ?? 0} t={t} />
                        <button style={ms.editAthleteBtn} onClick={() => onEditAthlete(record, a)}>
                          <PencilIcon size={13} />
                          {t("history.athletes.edit")}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Desktop table ──────────────────────────────────────────────────────────────
function DesktopTable({
  records,
  generatingId,
  formatDate,
  t,
  onDownload,
  expandedIds,
  onToggleExpand,
  onEditAthlete,
  coachId,
}: {
  records: UnifiedRecord[];
  generatingId: string | null;
  formatDate: (ts: any) => string;
  t: (k: string) => string;
  onDownload: (r: UnifiedRecord) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onEditAthlete: (r: UnifiedRecord, a: UnifiedAthleteRow) => void;
  coachId: string;
}) {
  return (
    <div style={s.tableCard}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={{ ...s.th, width: 32 }}></th>
            <th style={s.th}>{t("history.table.date")}</th>
            <th style={s.th}>{t("history.table.competition")}</th>
            <th style={s.th}>{t("history.table.athletes")}</th>
            <th style={s.th}>{t("history.table.total")}</th>
            <th style={s.th}>{t("history.table.refCode")}</th>
            <th style={s.th}>{t("history.table.receipt")}</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const isGenerating = generatingId === record.id;
            const expanded = expandedIds.has(record.id);
            const canEditAny = canCoachEditRegistration(
              { coachId } as any,
              record.competition,
              coachId
            );
            const wcd = record.competition ? getWeightChangeDeadline(record.competition) : null;
            const deadlineLabel = wcd ? formatDate(wcd) : null;

            return (
              <React.Fragment key={record.id}>
                <tr
                  style={{ ...s.tableRow, cursor: "pointer" }}
                  onClick={() => onToggleExpand(record.id)}
                >
                  <td style={{ ...s.td, color: C.text3, textAlign: "center" }}>
                    {expanded ? "▲" : "▼"}
                  </td>
                  <td style={s.td}>{formatDate(record.createdAt)}</td>
                  <td style={{ ...s.td, fontWeight: 600, color: C.text, maxWidth: 260 }}>
                    {record.competitionName}
                  </td>
                  <td style={s.td}>
                    <span style={s.athleteCount}>{record.athleteCount}</span>
                  </td>
                  <td style={{ ...s.td, fontWeight: 700, color: C.text }}>
                    {record.paymentType === "admin_manual" ? (
                      record.totalAmount > 0 ? (
                        <span style={{ color: C.blue }}>{record.totalAmount} GEL</span>
                      ) : (
                        <span style={s.manualBadge}>{t("history.adminLabel")}</span>
                      )
                    ) : record.paymentType === "prepaid" ? (
                      <span style={{ color: C.blue }}>{record.totalAmount} GEL</span>
                    ) : (
                      `${record.totalAmount} GEL`
                    )}
                  </td>
                  <td style={s.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={s.refCode}>{record.referenceCode}</span>
                      {record.paymentType === "admin_manual" && (
                        <span style={s.bankBadge}>{t("history.adminLabel")}</span>
                      )}
                      {record.paymentType === "prepaid" && (
                        <span style={s.prepaidBadge}>{t("history.prepaid")}</span>
                      )}
                    </div>
                  </td>
                  <td style={s.td} onClick={(e) => e.stopPropagation()}>
                    <button
                      style={{ ...s.pdfBtn, opacity: isGenerating ? 0.6 : 1, cursor: isGenerating ? "not-allowed" : "pointer" }}
                      onClick={() => !isGenerating && onDownload(record)}
                      disabled={isGenerating}
                    >
                      {isGenerating ? "⏳ Generating…" : `↓ ${t("history.downloadPdf")}`}
                    </button>
                  </td>
                </tr>
                {expanded && (
                  <tr style={s.tableRow}>
                    <td colSpan={7} style={{ padding: 0, backgroundColor: C.surface2 }}>
                      <div style={s.expandedContainer}>
                        {!canEditAny && deadlineLabel && (
                          <div style={s.deadlineHintRow}>
                            {t("history.athletes.editClosed").replace("{date}", deadlineLabel)}
                          </div>
                        )}
                        {canEditAny && deadlineLabel && (
                          <div style={s.editOpenRow}>
                            <InfoIcon size={13} color={C.blue} style={{ marginTop: 1 }} />
                            <span>{t("history.athletes.editOpen").replace("{date}", deadlineLabel)}</span>
                          </div>
                        )}
                        <table style={s.subTable}>
                          <thead>
                            <tr>
                              <th style={s.subTh}>{t("admin.allRegistrations.athleteName")}</th>
                              <th style={s.subTh}>{t("admin.allRegistrations.division")}</th>
                              <th style={s.subTh}>{t("admin.allRegistrations.weight")}</th>
                              <th style={s.subTh}>{t("admin.allRegistrations.category")}</th>
                              <th style={s.subTh}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {record.athletes.map((a, i) => (
                              <tr key={(a.registrationId ?? "") + i}>
                                <td style={s.subTd}>{a.firstName} {a.lastName}</td>
                                <td style={s.subTd}>{a.division}</td>
                                <td style={{ ...s.subTd, fontWeight: 700 }}>{a.weightCategory}</td>
                                <td style={s.subTd}>{a.category ?? "—"}</td>
                                <td style={{ ...s.subTd, textAlign: "right" }}>
                                  {canEditAny && a.registrationId ? (
                                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                                      <ChangeBudgetBadge used={a.weightChangeCount ?? 0} t={t} />
                                      <button
                                        style={s.editAthleteBtnDesktop}
                                        onClick={() => onEditAthlete(record, a)}
                                      >
                                        <PencilIcon size={13} />
                                        {t("history.athletes.edit")}
                                      </button>
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: 11, color: C.text3 }}>
                                      {a.registrationId ? "—" : ""}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: 1100, margin: "0 auto" },
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap",
  },
  pageLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: "2px",
    color: C.red, marginBottom: 6, textTransform: "uppercase",
  },
  heading: { fontFamily: FONT.display, letterSpacing: "2px", color: C.text, lineHeight: 1, marginBottom: 8 },
  subheading: { fontSize: 14, color: C.text2 },
  statsRow: { display: "flex", gap: 10, alignItems: "flex-start" },
  statBox: {
    backgroundColor: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "12px 18px", textAlign: "center", minWidth: 80,
  },
  statNumber: { fontFamily: FONT.display, fontSize: 26, color: C.text, lineHeight: 1 },
  statLabel: { fontSize: 9, color: C.text3, textTransform: "uppercase", letterSpacing: "1px", marginTop: 3 },

  // ── Year filter bar ──────────────────────────────────────────────────────────
  yearRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 24,
    padding: "14px 18px",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
  },
  yearRowLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: C.text3,
    flexShrink: 0,
  },
  yearBtnGroup: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  yearBtn: {
    fontFamily: FONT.body,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.5px",
    padding: "6px 16px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface2,
    color: C.text2,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  yearBtnActive: {
    backgroundColor: C.red,
    color: "#ffffff",
    border: `1px solid ${C.red}`,
  },

  stateBox: { textAlign: "center", padding: "80px 24px" },
  emptyIcon: { fontSize: 40, marginBottom: 16 },
  stateText: { fontSize: 16, color: C.text2, marginBottom: 8 },
  stateSub: { fontSize: 13, color: C.text3 },
  spinner: {
    width: 34, height: 34, margin: "0 auto",
    border: `3px solid ${C.border}`, borderTopColor: C.red,
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
  },

  // Desktop table
  tableCard: {
    backgroundColor: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 12, overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", padding: "11px 16px", backgroundColor: C.surface2,
    color: C.text2, fontWeight: 700, fontSize: 11,
    textTransform: "uppercase", letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  },
  tableRow: { borderBottom: `1px solid ${C.border}` },
  td: { padding: "13px 16px", color: C.text2, fontSize: 13, verticalAlign: "middle" },
  athleteCount: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: "50%",
    backgroundColor: C.surface2, border: `1px solid ${C.border}`,
    fontSize: 12, fontWeight: 700, color: C.text,
  },
  refCode: {
    fontFamily: "monospace", fontSize: 12, backgroundColor: C.surface2,
    padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.border}`,
    color: C.text, fontWeight: 600, letterSpacing: "1px",
  },
  bankBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: 4,
    padding: "2px 6px", fontSize: 9, fontWeight: 700,
    letterSpacing: "0.5px", border: "1px solid #93c5fd", textTransform: "uppercase",
  },
  prepaidBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: 4,
    padding: "2px 6px", fontSize: 9, fontWeight: 700,
    letterSpacing: "0.5px", border: `1px solid ${C.blue}`, textTransform: "uppercase",
  },
  manualBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: 4,
    padding: "3px 8px", fontSize: 11, fontWeight: 700, border: "1px solid #93c5fd",
  },
  pdfBtn: {
    backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border}`,
    borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600,
    whiteSpace: "nowrap", fontFamily: FONT.body,
  },

  // ── Expanded athletes panel (desktop) ─────────────────────────────────
  expandedContainer: {
    padding: "12px 16px",
    borderTop: `1px solid ${C.border}`,
  },
  deadlineHintRow: {
    fontSize: 12,
    color: C.text3,
    fontStyle: "italic",
    marginBottom: 8,
  },
  editOpenRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    fontSize: 12,
    color: C.blue,
    backgroundColor: C.blueDim,
    border: `1px solid ${C.blue}`,
    borderRadius: 6,
    padding: "6px 10px",
    marginBottom: 10,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: C.surface2,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    whiteSpace: "nowrap",
  },
  toast: {
    position: "fixed",
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: C.success,
    color: "#fff",
    padding: "12px 22px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: FONT.body,
    boxShadow: "0 8px 28px rgba(0,0,0,0.22)",
    zIndex: 2000,
    letterSpacing: "0.3px",
  },
  subTable: { width: "100%", borderCollapse: "collapse" },
  subTh: {
    textAlign: "left",
    padding: "6px 10px",
    fontSize: 10,
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`,
  },
  subTd: {
    padding: "8px 10px",
    fontSize: 13,
    color: C.text2,
    borderBottom: `1px solid ${C.border}`,
  },
  editAthleteBtnDesktop: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    backgroundColor: C.blue,
    color: "#fff",
    border: `1px solid ${C.blue}`,
    borderRadius: 6,
    padding: "5px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    boxShadow: "0 1px 3px rgba(59,130,246,0.35)",
  },

  // ── Search + chip filter bar (mobile-friendly, wraps vertically) ─────────
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 16,
  },
  filterSearch: {
    flex: "1 1 240px",
    minWidth: 0,
    padding: "9px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: 7,
    fontSize: 13,
    backgroundColor: C.surface2,
    color: C.text,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: FONT.body,
  },
  chipGroup: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: 20,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    minHeight: 32,
    fontFamily: FONT.body,
  },
  chipActive: {
    backgroundColor: C.red,
    color: "#fff",
    borderColor: C.red,
  },
};

// ── Mobile card styles ─────────────────────────────────────────────────────────
const ms: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 12, padding: "14px 16px",
    borderLeft: "3px solid transparent",
    display: "flex", flexDirection: "column", gap: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  topRow: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", gap: 8,
  },
  compName: { fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.3, flex: 1 },
  date: { fontSize: 11, color: C.text3, whiteSpace: "nowrap", marginTop: 2, flexShrink: 0 },
  midRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  refCode: {
    fontFamily: "monospace", fontSize: 11, backgroundColor: C.surface2,
    padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.border}`,
    color: C.text, fontWeight: 600, letterSpacing: "0.8px",
  },
  bankBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: 4,
    padding: "2px 6px", fontSize: 9, fontWeight: 700,
    letterSpacing: "0.5px", border: "1px solid #93c5fd", textTransform: "uppercase",
  },
  prepaidBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: 4,
    padding: "2px 6px", fontSize: 9, fontWeight: 700,
    letterSpacing: "0.5px", border: `1px solid ${C.blue}`, textTransform: "uppercase",
  },
  bottomRow: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 8,
  },
  bottomLeft: { display: "flex", alignItems: "center", gap: 10 },
  athletePill: {
    display: "flex", alignItems: "center", gap: 5,
    backgroundColor: C.surface2, border: `1px solid ${C.border}`,
    borderRadius: 20, padding: "4px 10px",
  },
  athletePillNum: { fontSize: 13, fontWeight: 700, color: C.text },
  athletePillLabel: { fontSize: 10, color: C.text3, textTransform: "uppercase", letterSpacing: "0.5px" },
  amount: { fontSize: 14, fontWeight: 700 },
  manualBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: 4,
    padding: "3px 8px", fontSize: 11, fontWeight: 700, border: "1px solid #93c5fd",
  },
  pdfBtn: {
    backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700,
    whiteSpace: "nowrap", fontFamily: FONT.body, flexShrink: 0, minHeight: 36,
  },
  expandBtn: {
    backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700,
    whiteSpace: "nowrap", fontFamily: FONT.body, flexShrink: 0, minHeight: 36,
  },
  expandedPanel: {
    marginTop: 8,
    padding: "10px 12px",
    backgroundColor: C.surface2,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  deadlineHint: {
    fontSize: 11,
    color: C.text3,
    fontStyle: "italic",
  },
  editOpenHint: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    fontSize: 11.5,
    color: C.blue,
    backgroundColor: C.blueDim,
    border: `1px solid ${C.blue}`,
    borderRadius: 6,
    padding: "6px 10px",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  athleteRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    backgroundColor: C.surface,
    borderRadius: 6,
    border: `1px solid ${C.border}`,
  },
  athleteName: { fontSize: 13, fontWeight: 700, color: C.text },
  athleteMeta: { fontSize: 11, color: C.text3, marginTop: 2 },
  editAthleteBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    backgroundColor: C.blue,
    color: "#fff",
    border: `1px solid ${C.blue}`,
    borderRadius: 6,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    whiteSpace: "nowrap",
    minHeight: 34,
    boxShadow: "0 1px 3px rgba(59,130,246,0.35)",
  },
};
